/**
 * CalendarMonthGrid — read-only month-grid visual for academic calendar
 * events. Used by both the per-organization Calendar tab and the
 * cross-org /calendar page so the visual stays consistent.
 *
 * Key design choices (driven by user feedback):
 * - Single month per page so each cell can be wide enough to hold the
 *   date number + a stack of per-type chips (Power BI-style cells).
 * - Custom DayButton renders the chips inline. Up to 3 visible per cell;
 *   anything beyond shows as "+N more". Hovering the cell exposes the
 *   full breakdown via the title attribute.
 * - Click is intentionally inert (cursor:default); selection visuals
 *   are suppressed in CSS.
 * - Default month is picked by `pickDefaultMonth()`: the current month
 *   if it has events, else the first future month with events, else
 *   today's month.
 *
 * On the org Calendar tab, events have only `typeCode`. On the cross-org
 * /calendar page, events also carry `orgLabel`, which we group into
 * "<TypeCode> (<count>): <orgs>" chips so a date with overlap doesn't
 * fall apart visually.
 */

import { useMemo } from 'react';
import {
  DayPicker,
  type DayButtonProps,
} from 'react-day-picker';
import 'react-day-picker/style.css';
import './CalendarMonthGrid.css';

import { fromIsoDate, toIsoDate } from './calendarYearUtils';

export interface CalendarGridEvent {
  /** ISO date YYYY-MM-DD */
  date: string;
  /** SpecialDayType codeValue (e.g. "Inservice", "Graduation"). */
  typeCode: string;
  /** Optional org name for cross-org views. Omitted on per-org views. */
  orgLabel?: string;
}

interface CalendarMonthGridProps {
  events: CalendarGridEvent[];
  /** Constrain navigation to the school year. */
  fromDate?: Date;
  toDate?: Date;
  /** Override the default starting month. If omitted, uses pickDefaultMonth. */
  defaultMonth?: Date;
  /** Header above the grid. */
  title?: string;
  /**
   * When true, render one chip per district instead of collapsing multi-district
   * days to "Label (count)". Cells grow to fit (see .cmg-expand in the CSS).
   * Only meaningful for cross-org events (those carrying `orgLabel`).
   */
  expandByOrg?: boolean;
}

// ---------------------------------------------------------------------------
// Type metadata: codes, abbreviations, colors, labels
// ---------------------------------------------------------------------------

const TYPE_LABELS: Record<string, string> = {
  Inservice: 'Inservice',
  Clerical: 'Clerical',
  ProfessionalDevelopment: 'Prof. Development',
  Closed: 'Closed (No School)',
  Graduation: 'Graduation',
  // Synthetic bound-day markers (see BOUND_DAY_TYPE_OPTIONS).
  StudentFirstDay: 'First Day (Students)',
  StudentLastDay: 'Last Day (Students)',
  TeacherFirstDay: 'First Day (Teachers)',
  TeacherLastDay: 'Last Day (Teachers)',
};

/** Single- or two-letter abbreviations for use inside compact day-cell chips. */
const TYPE_ABBR: Record<string, string> = {
  Inservice: 'I',
  Clerical: 'C',
  ProfessionalDevelopment: 'PD',
  Closed: 'X',
  Graduation: 'G',
  StudentFirstDay: 'SF',
  StudentLastDay: 'SL',
  TeacherFirstDay: 'TF',
  TeacherLastDay: 'TL',
};

// Chip colors: solid backgrounds (no alpha) so text-on-chip contrast is
// stable regardless of what's behind the chip. Text/bg ratios all clear
// AAA (~9:1) on both light and dark themes.
const TYPE_CHIP_CLASSES: Record<string, string> = {
  Inservice: 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100',
  Clerical: 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100',
  ProfessionalDevelopment:
    'bg-violet-100 text-violet-900 dark:bg-violet-900 dark:text-violet-100',
  Closed: 'bg-red-100 text-red-900 dark:bg-red-900 dark:text-red-100',
  Graduation:
    'bg-emerald-100 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100',
  StudentFirstDay:
    'bg-green-100 text-green-900 dark:bg-green-900 dark:text-green-100',
  StudentLastDay:
    'bg-orange-100 text-orange-900 dark:bg-orange-900 dark:text-orange-100',
  TeacherFirstDay:
    'bg-teal-100 text-teal-900 dark:bg-teal-900 dark:text-teal-100',
  TeacherLastDay:
    'bg-pink-100 text-pink-900 dark:bg-pink-900 dark:text-pink-100',
};

// Tailwind classes for the small color swatches that appear in legends and
// next to type titles. Exported so the edit modal can color-code its
// per-type sections to match the calendar's legend.
export const TYPE_SWATCHES: Record<string, string> = {
  Inservice: 'bg-blue-500',
  Clerical: 'bg-amber-500',
  ProfessionalDevelopment: 'bg-violet-500',
  Closed: 'bg-red-500',
  Graduation: 'bg-emerald-500',
  StudentFirstDay: 'bg-green-500',
  StudentLastDay: 'bg-orange-500',
  TeacherFirstDay: 'bg-teal-500',
  TeacherLastDay: 'bg-pink-500',
};

const FALLBACK_CHIP =
  'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';

const TYPE_DISPLAY_ORDER = [
  'Inservice',
  'Clerical',
  'ProfessionalDevelopment',
  'Closed',
  'Graduation',
  'StudentFirstDay',
  'StudentLastDay',
  'TeacherFirstDay',
  'TeacherLastDay',
];

function typeOrder(typeCode: string): number {
  const ix = TYPE_DISPLAY_ORDER.indexOf(typeCode);
  return ix === -1 ? 99 : ix;
}

/** Exported so the table-row badges in CalendarTab/CalendarPage stay in sync. */
export const TYPE_BADGE_CLASSNAMES = TYPE_CHIP_CLASSES;
export { TYPE_LABELS, TYPE_ABBR };

// ---------------------------------------------------------------------------
// Synthetic "bound day" types (student/teacher first/last day)
//
// These four dates live as scalar fields on the calendar record, not as
// special-day rows. We surface them as if they were special days. Each carries
// a negative sentinel "descriptor id" so it can be toggled in the cross-org
// day-type filter and saved ICS feeds alongside the real types (real descriptor
// ids are always positive). Keep in sync with BOUND_DAY_TYPES in
// backend/app/routers/calendar.py.
// ---------------------------------------------------------------------------

export const BOUND_DAY_TYPE_OPTIONS: Array<{
  id: number;
  code: string;
  label: string;
}> = [
  { id: -101, code: 'StudentFirstDay', label: 'First Day (Students)' },
  { id: -102, code: 'StudentLastDay', label: 'Last Day (Students)' },
  { id: -103, code: 'TeacherFirstDay', label: 'First Day (Teachers)' },
  { id: -104, code: 'TeacherLastDay', label: 'Last Day (Teachers)' },
];

/**
 * Build month-grid events for a calendar's four scalar bound-dates so the
 * per-org Calendar tab can show them alongside the special days. Null dates
 * are skipped. Accepts a structural type to stay decoupled from the API model.
 */
export function boundDayEventsFromCalendar(calendar: {
  studentStartDate: string | null;
  studentEndDate: string | null;
  teacherStartDate: string | null;
  teacherEndDate: string | null;
}): CalendarGridEvent[] {
  const pairs: Array<[string | null, string]> = [
    [calendar.studentStartDate, 'StudentFirstDay'],
    [calendar.studentEndDate, 'StudentLastDay'],
    [calendar.teacherStartDate, 'TeacherFirstDay'],
    [calendar.teacherEndDate, 'TeacherLastDay'],
  ];
  return pairs
    .filter(([date]) => !!date)
    .map(([date, typeCode]) => ({ date: date as string, typeCode }));
}

// ---------------------------------------------------------------------------
// Default-month logic
// ---------------------------------------------------------------------------

/**
 * Pick the most useful month to land on, given the events:
 *   1. If today's month has events, show today's month.
 *   2. Otherwise, show the earliest future month that has events.
 *   3. Otherwise (no future events), show today's month.
 *
 * Honors fromDate/toDate as outer bounds.
 */
export function pickDefaultMonth(
  events: CalendarGridEvent[],
  fromDate?: Date,
  toDate?: Date
): Date {
  const today = new Date();
  const startOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const clamp = (d: Date): Date => {
    if (fromDate && d < new Date(fromDate.getFullYear(), fromDate.getMonth(), 1)) {
      return new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
    }
    if (toDate && d > new Date(toDate.getFullYear(), toDate.getMonth(), 1)) {
      return new Date(toDate.getFullYear(), toDate.getMonth(), 1);
    }
    return d;
  };

  if (events.length === 0) {
    return clamp(startOfThisMonth);
  }

  const eventDates = events
    .map((e) => fromIsoDate(e.date))
    .sort((a, b) => a.getTime() - b.getTime());

  // Does this month have events?
  const thisMonthHasEvents = eventDates.some(
    (d) =>
      d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth()
  );
  if (thisMonthHasEvents) return clamp(startOfThisMonth);

  // Otherwise, first future event
  const future = eventDates.find((d) => d >= startOfThisMonth);
  if (future) return clamp(new Date(future.getFullYear(), future.getMonth(), 1));

  // No events at or after today's month — fall back to today's month
  return clamp(startOfThisMonth);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface DayCellEntry {
  typeCode: string;
  count: number;
  orgs: string[]; // empty when no orgLabel was provided
}

function buildDayEntries(eventsForDay: CalendarGridEvent[]): DayCellEntry[] {
  const byType = new Map<string, DayCellEntry>();
  for (const ev of eventsForDay) {
    const entry = byType.get(ev.typeCode) ?? {
      typeCode: ev.typeCode,
      count: 0,
      orgs: [],
    };
    entry.count += 1;
    if (ev.orgLabel) entry.orgs.push(ev.orgLabel);
    byType.set(ev.typeCode, entry);
  }
  return Array.from(byType.values()).sort(
    (a, b) => typeOrder(a.typeCode) - typeOrder(b.typeCode)
  );
}

function describeDay(date: Date, entries: DayCellEntry[]): string {
  if (entries.length === 0) return '';
  const lines = entries.map((e) => {
    const label = TYPE_LABELS[e.typeCode] ?? e.typeCode;
    if (e.orgs.length === 0) {
      return e.count > 1 ? `${label} (×${e.count})` : label;
    }
    return `${label} (${e.count}): ${e.orgs.join(', ')}`;
  });
  const dateLabel = date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
  return `${dateLabel}\n${lines.join('\n')}`;
}

const MAX_VISIBLE_CHIPS = 3;

export const CalendarMonthGrid: React.FC<CalendarMonthGridProps> = ({
  events,
  fromDate,
  toDate,
  defaultMonth,
  title,
  expandByOrg = false,
}) => {
  // Group events by ISO date for fast lookup inside DayButton
  const eventsByDate = useMemo(() => {
    const m = new Map<string, CalendarGridEvent[]>();
    for (const ev of events) {
      const list = m.get(ev.date) ?? [];
      list.push(ev);
      m.set(ev.date, list);
    }
    return m;
  }, [events]);

  const presentTypes = useMemo(() => {
    const seen = new Set<string>();
    const counts = new Map<string, number>();
    for (const ev of events) {
      seen.add(ev.typeCode);
      counts.set(ev.typeCode, (counts.get(ev.typeCode) ?? 0) + 1);
    }
    return Array.from(seen)
      .sort((a, b) => typeOrder(a) - typeOrder(b))
      .map((code) => ({ code, count: counts.get(code) ?? 0 }));
  }, [events]);

  const initialMonth =
    defaultMonth ?? pickDefaultMonth(events, fromDate, toDate);

  // Custom DayButton: renders the date number plus per-type chips.
  // Defined inside the component so it closes over eventsByDate without
  // needing to thread it through props.
  const DayButton: React.FC<DayButtonProps> = ({
    day,
    modifiers,
    ...buttonProps
  }) => {
    const iso = toIsoDate(day.date);
    const dayEvents = eventsByDate.get(iso) ?? [];
    const entries = buildDayEntries(dayEvents);
    const tooltip = describeDay(day.date, entries);

    // Two render modes:
    //  - grouped (default): one chip per type; a multi-district type collapses
    //    to "Label (count)" with the names available in the tooltip. Capped at
    //    MAX_VISIBLE_CHIPS, the rest summarized as "+N more".
    //  - expanded (expandByOrg): one chip per district, names inline, uncapped.
    //    The cell grows to fit (see .cmg-expand in CalendarMonthGrid.css).
    let chips: Array<{ key: string; chipClass: string; abbr: string; text: string }>;
    let overflow = 0;

    if (expandByOrg) {
      const sorted = [...dayEvents].sort((a, b) => {
        const t = typeOrder(a.typeCode) - typeOrder(b.typeCode);
        return t !== 0 ? t : (a.orgLabel ?? '').localeCompare(b.orgLabel ?? '');
      });
      chips = sorted.map((ev, i) => ({
        key: `${ev.typeCode}-${ev.orgLabel ?? ''}-${i}`,
        chipClass: TYPE_CHIP_CLASSES[ev.typeCode] ?? FALLBACK_CHIP,
        abbr: TYPE_ABBR[ev.typeCode] ?? ev.typeCode.charAt(0),
        text: ev.orgLabel ?? TYPE_LABELS[ev.typeCode] ?? ev.typeCode,
      }));
    } else {
      const visible = entries.slice(0, MAX_VISIBLE_CHIPS);
      overflow = entries.length - visible.length;
      chips = visible.map((entry) => {
        const fullLabel = TYPE_LABELS[entry.typeCode] ?? entry.typeCode;
        const showOrg = entry.orgs.length === 1 ? entry.orgs[0] : null;
        const text = showOrg
          ? showOrg
          : entry.count > 1
            ? `${fullLabel} (${entry.count})`
            : fullLabel;
        return {
          key: entry.typeCode,
          chipClass: TYPE_CHIP_CLASSES[entry.typeCode] ?? FALLBACK_CHIP,
          abbr: TYPE_ABBR[entry.typeCode] ?? entry.typeCode.charAt(0),
          text,
        };
      });
    }

    return (
      <button
        {...buttonProps}
        title={tooltip || undefined}
        aria-label={tooltip || undefined}
        className={buttonProps.className}
      >
        <span
          className={`block text-[11px] leading-none ${
            modifiers.outside
              ? 'text-gray-500 dark:text-gray-400'
              : 'text-gray-700 dark:text-gray-200'
          } text-right`}
        >
          {day.date.getDate()}
        </span>
        {chips.length > 0 && (
          <span
            className={`mt-1 flex flex-col gap-0.5 ${
              expandByOrg ? '' : 'overflow-hidden'
            }`}
          >
            {chips.map((chip) => (
              <span
                key={chip.key}
                className={`text-[10px] leading-tight px-1 py-0.5 rounded truncate ${chip.chipClass}`}
              >
                <span className="font-semibold mr-1">{chip.abbr}</span>
                <span className="font-normal">{chip.text}</span>
              </span>
            ))}
            {overflow > 0 && (
              <span className="text-[10px] leading-tight px-1 text-gray-500 dark:text-gray-400">
                +{overflow} more
              </span>
            )}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-3">
      {title && (
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">
          {title}
        </h4>
      )}

      <div className={expandByOrg ? 'cmg-big cmg-expand' : 'cmg-big'}>
        <DayPicker
          mode="single"
          selected={undefined}
          onSelect={() => {}}
          numberOfMonths={1}
          startMonth={fromDate}
          endMonth={toDate}
          defaultMonth={initialMonth}
          components={{ DayButton }}
          showOutsideDays
        />
      </div>

      {presentTypes.length > 0 && (
        <div className="flex flex-wrap gap-3 text-xs text-gray-700 dark:text-gray-300 pt-1">
          {presentTypes.map(({ code, count }) => {
            const swatch = TYPE_SWATCHES[code] ?? 'bg-gray-400';
            const label = TYPE_LABELS[code] ?? code;
            return (
              <span key={code} className="inline-flex items-center gap-1.5">
                <span className={`inline-block h-2.5 w-2.5 rounded-sm ${swatch}`} />
                <span>
                  {label}{' '}
                  <span className="text-gray-500 dark:text-gray-400">
                    ({count})
                  </span>
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};
